import { Plus, X, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EscalationSettings, ReportDigestFrequency, ReportMode, ReportModeSelector } from "@/components/agent/details/ReportModeSelector";
import { SetupAnalysisResult } from '@/types/agent/setup';
import { ReviewSectionCard } from "./ReviewSectionCard";
import { GenieCategories } from "@/types/shared/categories";
import { useState } from "react";

interface ConfirmationGoalsSectionProps {
  analysis: SetupAnalysisResult;
  onChange: (field: keyof SetupAnalysisResult, value: any) => void;
  onSupportEmailsChange: (emails: string[]) => void;
}

export const ConfirmationGoalsSection = ({
  analysis,
  onChange,
  onSupportEmailsChange,
}: ConfirmationGoalsSectionProps) => {
  const category = GenieCategories.find((cat) => cat.id === analysis.category) || GenieCategories.find((cat) => cat.id === 'other')!;
  const [emailErrors, setEmailErrors] = useState<{ [key: number]: string }>({});
  // No genie exists yet during setup, so there's nothing to check a Slack
  // connection against — Slack escalation channel selection happens later,
  // from the genie's own Conversation Reports card.
  const hasSlackConnection = false;

  const reportMode: ReportMode = analysis.reportMode ?? (analysis.sendConversationReports ?? true ? "every_conversation" : "off");

  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Allow empty for now
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addSupportEmail = () => {
    const currentEmails = analysis.supportEmails || [];
    onSupportEmailsChange([...currentEmails, ""]);
  };

  const removeSupportEmail = (index: number) => {
    const currentEmails = analysis.supportEmails || [];
    onSupportEmailsChange(currentEmails.filter((_, i) => i !== index));
    // Remove error for this index
    const newErrors = { ...emailErrors };
    delete newErrors[index];
    setEmailErrors(newErrors);
  };

  const updateSupportEmail = (index: number, email: string) => {
    const currentEmails = analysis.supportEmails || [];
    const updatedEmails = [...currentEmails];
    updatedEmails[index] = email;
    onSupportEmailsChange(updatedEmails);

    // Real-time validation
    const newErrors = { ...emailErrors };
    if (email && !validateEmail(email)) {
      newErrors[index] = "Please enter a valid email address";
    } else {
      delete newErrors[index];
    }
    setEmailErrors(newErrors);
  };

  return (
    <ReviewSectionCard
      title="What should your genie collect?"
      subtitle="Define the information you need from conversations"
      genieIcon="lightbulb"
      categoryColor={category.color}
    >
      <div className="space-y-8">
        {/* Collection Goal - Now Editable */}
        <div>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between md:gap-3 mb-2">
            <Label htmlFor="goal" className="text-xs sm:text-sm font-medium">
              Information to collect
            </Label>
            <p className="text-xs text-[#4E9CFF] font-semibold text-left md:text-right md:flex-1 mt-2 md:mt-0">
              We'll create structured goals for your genie based on what you describe here, making it easy to track collected information.
            </p>
          </div>
          <Textarea
            id="goal"
            value={analysis.goal || ""}
            onChange={(e) => onChange("goal", e.target.value)}
            className="text-sm p-3 min-h-[100px] mt-2"
            rows={3}
            placeholder="e.g., Collect lead's name, email, company size, budget, and pain points"
          />
        </div>

        {/* Reporting Mode */}
        <div className="border-2 border-gray-200 rounded-xl bg-white p-5">
          <div className="mb-5">
            <Label className="text-base sm:text-lg font-semibold text-gray-900">
              Reporting Mode
            </Label>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Choose how your team hears about conversations
            </p>
          </div>

          <ReportModeSelector
            reportMode={reportMode}
            onReportModeChange={(mode) => onChange("reportMode", mode)}
            digestFrequency={analysis.reportDigestFrequency ?? null}
            onDigestFrequencyChange={(frequency: ReportDigestFrequency) => onChange("reportDigestFrequency", frequency)}
            digestDayOfWeek={analysis.reportDigestDayOfWeek ?? 1}
            onDigestDayOfWeekChange={(day) => onChange("reportDigestDayOfWeek", day)}
            escalationSettings={analysis.escalationSettings ?? {}}
            onEscalationSettingsChange={(settings: EscalationSettings) => onChange("escalationSettings", settings)}
            hasSlackConnection={hasSlackConnection}
            decisionPrompt={analysis.autoReportDecisionPrompt ?? null}
            onDecisionPromptChange={(prompt) => onChange("autoReportDecisionPrompt", prompt)}
            onRegenerateDecisionPrompt={() => onChange("autoReportDecisionPrompt", null)}
            brand={category.color}
          />

          {(reportMode === "auto" || reportMode === "every_conversation" || reportMode === "digest") && (
            <div className="mt-5">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Email Recipients</Label>
              <div className="space-y-3 mb-4">
                {(analysis.supportEmails || []).map((email, index) => (
                  <div key={index}>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => updateSupportEmail(index, e.target.value)}
                          className={`h-10 text-sm ${emailErrors[index] ? 'border-red-500 focus:ring-red-500' : ''}`}
                          placeholder="support@example.com"
                        />
                        {emailErrors[index] && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
                            <AlertCircle className="w-3 h-3" />
                            <span>{emailErrors[index]}</span>
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeSupportEmail(index)}
                        className="px-2 sm:px-3 text-[#FF4DB8] hover:bg-[#FF4DB8]/10 border-[#FF4DB8]/30 h-11"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {(analysis.supportEmails || []).length === 0 && (
                  <div className="text-center py-4 px-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-xs sm:text-sm text-gray-600">No email addresses added yet</p>
                  </div>
                )}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSupportEmail}
                className="w-full text-sm h-11"
                style={{
                  borderColor: `${category.color}40`,
                  color: category.color
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Email Address
              </Button>
            </div>
          )}
        </div>
      </div>
    </ReviewSectionCard>
  );
};
